// Shared, DOM-free board cleaning primitives for the snapshot recognition
// paths (English OCR + Math/Physics geometry).
//
// The previous approach used a single global percentile ("the brightest 2% of
// the frame is chalk"). On a real classroom board that fails hard:
//   - a teacher's white shirt / hand is brighter than chalk and eats the whole
//     2% budget, so the chalk vanishes and the shirt becomes the "writing";
//   - glare spots do the same;
//   - uneven lighting means one global cut-off cannot fit both sides of the board;
//   - the fixed 2% is wrong for both a nearly empty board and a very full one.
//
// Local (illumination-normalized) thresholding fixes all four: a pixel is chalk
// only if it is brighter than its own neighbourhood. The inside of a large
// bright surface is NOT brighter than its neighbourhood, so shirts and glare
// collapse to thin outlines, which the component filter below then rejects.

export interface Bbox { x0: number; y0: number; x1: number; y1: number }

/** Box-sum integral image (width+1) x (height+1), so any window sums in O(1). */
export function integralImage(values: Float32Array, width: number, height: number): Float64Array {
  const stride = width + 1
  const integral = new Float64Array(stride * (height + 1))
  for (let y = 0; y < height; y++) {
    let rowSum = 0
    for (let x = 0; x < width; x++) {
      rowSum += values[y * width + x]
      integral[(y + 1) * stride + (x + 1)] = integral[y * stride + (x + 1)] + rowSum
    }
  }
  return integral
}

/** Mean of the (2r+1) window centred on (x,y), clipped to the image. */
export function windowMean(integral: Float64Array, width: number, height: number, x: number, y: number, radius: number): number {
  const stride = width + 1
  const x0 = Math.max(0, x - radius)
  const y0 = Math.max(0, y - radius)
  const x1 = Math.min(width - 1, x + radius)
  const y1 = Math.min(height - 1, y + radius)
  const sum = integral[(y1 + 1) * stride + (x1 + 1)]
    - integral[y0 * stride + (x1 + 1)]
    - integral[(y1 + 1) * stride + x0]
    + integral[y0 * stride + x0]
  return sum / ((x1 - x0 + 1) * (y1 - y0 + 1))
}

/**
 * Chalk mask by local contrast. `brightness` is the chalk-positive channel
 * (already polarity-corrected: chalk high, board low). A pixel is chalk when it
 * exceeds its local mean by `bias`, which keeps thin strokes (i, l, t, commas,
 * decimal points, minus signs) that a global cut-off or a morphological open
 * would erase.
 */
export function localContrastMask(brightness: Float32Array, width: number, height: number, radius: number, bias: number): Uint8Array {
  const integral = integralImage(brightness, width, height)
  const mask = new Uint8Array(width * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x
      mask[index] = brightness[index] > windowMean(integral, width, height, x, y, radius) + bias ? 1 : 0
    }
  }
  return mask
}

/** Sensible neighbourhood: wide enough to span a stroke, small enough to track lighting. */
export function maskRadius(width: number, height: number): number {
  return Math.max(8, Math.round(Math.min(width, height) / 16))
}

export interface MaskComponent {
  area: number
  x0: number; y0: number; x1: number; y1: number
}

/** area / bounding-box area. Handwriting is sparse (~0.1-0.4); a shirt or glare patch is solid. */
export function fillRatio(component: MaskComponent): number {
  const boxArea = (component.x1 - component.x0 + 1) * (component.y1 - component.y0 + 1)
  return boxArea > 0 ? component.area / boxArea : 0
}

export interface ComponentFilterOptions {
  width: number
  height: number
  /** Absolute floor. Kept low so punctuation and dots on "i" survive. */
  minArea?: number
  /** Reject solid blobs (person, glare) once they are also physically large. */
  maxFillRatio?: number
  /** A blob only counts as "large" past this fraction of the frame. */
  largeBoxFraction?: number
  /**
   * Text-only guard: drop components taller than this multiple of the median
   * component height. Local thresholding turns a teacher's shirt or a glare
   * patch into a large THIN OUTLINE - big bbox, low fill - which slips past the
   * solid-blob rule above. Leave unset for geometry (a triangle or a pendulum
   * string is legitimately tall and thin).
   */
  maxHeightRatio?: number
}

/**
 * Drops chalk-dust specks and solid bright blobs (teacher's clothing, hands,
 * glare). Only rejects a solid blob when it is BOTH solid and large, so a
 * legitimately filled small shape (a pendulum bob, a full stop) is kept.
 */
export function filterNoiseComponents(components: MaskComponent[], options: ComponentFilterOptions): MaskComponent[] {
  const { width, height } = options
  const minArea = options.minArea ?? 6
  const maxFillRatio = options.maxFillRatio ?? 0.55
  const largeBoxFraction = options.largeBoxFraction ?? 0.06
  const frameArea = Math.max(1, width * height)

  let heightLimit = Infinity
  if (options.maxHeightRatio && components.length >= 3) {
    // AREA-WEIGHTED median height. A plain median is dominated by chalk-dust
    // specks, which drags the limit down until real letters are the outliers
    // and get wiped - measured at 0% OCR accuracy before this weighting.
    const byHeight = [...components].sort((a, b) => (a.y1 - a.y0) - (b.y1 - b.y0))
    const totalArea = byHeight.reduce((sum, c) => sum + c.area, 0)
    let accumulated = 0
    let median = 0
    for (const component of byHeight) {
      accumulated += component.area
      if (accumulated * 2 >= totalArea) { median = component.y1 - component.y0 + 1; break }
    }
    if (median > 0) heightLimit = median * options.maxHeightRatio
  }

  const kept = components.filter((component) => {
    if (component.area < minArea) return false
    const boxArea = (component.x1 - component.x0 + 1) * (component.y1 - component.y0 + 1)
    const isLarge = boxArea / frameArea >= largeBoxFraction
    if (isLarge && fillRatio(component) > maxFillRatio) return false
    if (component.y1 - component.y0 + 1 > heightLimit) return false
    return true
  })
  // Safety net: if the height guard would throw away most of the ink it is
  // misfiring on this board, so fall back to the size rules alone.
  const inkOf = (list: MaskComponent[]) => list.reduce((sum, c) => sum + c.area, 0)
  if (heightLimit < Infinity && inkOf(kept) * 2 < inkOf(components)) {
    return components.filter((component) => {
      if (component.area < minArea) return false
      const boxArea = (component.x1 - component.x0 + 1) * (component.y1 - component.y0 + 1)
      return !(boxArea / frameArea >= largeBoxFraction && fillRatio(component) > maxFillRatio)
    })
  }
  return kept
}

/**
 * ROI over the meaningful components only. Components far smaller than the
 * largest survivor are excluded from the box (a single surviving speck in a
 * corner must not stretch the ROI over the whole board) but are NOT deleted
 * from the mask. `pad` keeps letters from being clipped.
 */
export function contentBboxFromComponents(
  components: MaskComponent[],
  width: number,
  height: number,
  pad = 8,
  minRelativeArea = 0.02,
): Bbox | null {
  if (components.length === 0) return null
  const largest = components.reduce((max, component) => Math.max(max, component.area), 0)
  const kept = components.filter((component) => component.area >= largest * minRelativeArea)
  const source = kept.length > 0 ? kept : components
  let x0 = width, y0 = height, x1 = -1, y1 = -1
  for (const component of source) {
    if (component.x0 < x0) x0 = component.x0
    if (component.y0 < y0) y0 = component.y0
    if (component.x1 > x1) x1 = component.x1
    if (component.y1 > y1) y1 = component.y1
  }
  if (x1 < x0 || y1 < y0) return null
  return {
    x0: Math.max(0, x0 - pad),
    y0: Math.max(0, y0 - pad),
    x1: Math.min(width - 1, x1 + pad),
    y1: Math.min(height - 1, y1 + pad),
  }
}

export interface LabeledMask {
  components: MaskComponent[]
  labels: Int32Array
}

/** 8-connectivity labeling that also returns the per-pixel label, so rejected components can be erased. */
export function labelMask(mask: Uint8Array, width: number, height: number): LabeledMask {
  const total = width * height
  const labels = new Int32Array(total).fill(-1)
  const stack = new Int32Array(total)
  const components: MaskComponent[] = []
  for (let i = 0; i < total; i++) {
    if (mask[i] !== 1 || labels[i] !== -1) continue
    const id = components.length
    let area = 0
    let x0 = width, x1 = -1, y0 = height, y1 = -1
    let sp = 0
    stack[sp++] = i
    labels[i] = id
    while (sp > 0) {
      const p = stack[--sp]
      area += 1
      const x = p % width
      const y = (p / width) | 0
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue
          const nx = x + dx
          const ny = y + dy
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue
          const n = ny * width + nx
          if (mask[n] === 1 && labels[n] === -1) { labels[n] = id; stack[sp++] = n }
        }
      }
    }
    components.push({ area, x0, x1, y0, y1 })
  }
  return { components, labels }
}

/**
 * Full clean-up pass: label, drop dust and solid bright blobs, erase the
 * rejected pixels in place, and return the surviving components.
 */
export function cleanMask(mask: Uint8Array, width: number, height: number, options?: Omit<ComponentFilterOptions, 'width' | 'height'>): MaskComponent[] {
  const { components, labels } = labelMask(mask, width, height)
  const kept = filterNoiseComponents(components, { width, height, ...options })
  const keptIds = new Set<number>()
  const keptSet = new Set(kept)
  components.forEach((component, id) => { if (keptSet.has(component)) keptIds.add(id) })
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] === 1 && !keptIds.has(labels[i])) mask[i] = 0
  }
  return kept
}

/**
 * Groups components into text lines by vertical overlap. A real classroom board
 * usually carries several unrelated things at once (old lines, board-edge
 * fragments, a stray tick); one global bounding box over all of them hands OCR a
 * mostly-empty region with the actual word lost in a corner.
 */
export function groupIntoLines(components: MaskComponent[]): MaskComponent[][] {
  const sorted = [...components].sort((a, b) => (a.y0 + a.y1) - (b.y0 + b.y1))
  const groups: { y0: number; y1: number; items: MaskComponent[] }[] = []
  for (const component of sorted) {
    const height = component.y1 - component.y0 + 1
    const group = groups.find((candidate) => {
      const overlap = Math.min(candidate.y1, component.y1) - Math.max(candidate.y0, component.y0) + 1
      return overlap >= 0.5 * Math.min(height, candidate.y1 - candidate.y0 + 1)
    })
    if (group) {
      group.y0 = Math.min(group.y0, component.y0)
      group.y1 = Math.max(group.y1, component.y1)
      group.items.push(component)
    } else {
      groups.push({ y0: component.y0, y1: component.y1, items: [component] })
    }
  }
  return groups.map((group) => group.items)
}

/**
 * Keeps the dominant text line plus any other line of comparable weight, so a
 * two-line sentence survives while a stray tick on the board edge is dropped.
 */
export function selectTextGroups(components: MaskComponent[], minGroupAreaRatio = 0.25): MaskComponent[] {
  const groups = groupIntoLines(components)
  if (groups.length <= 1) return components
  const weights = groups.map((group) => group.reduce((sum, component) => sum + component.area, 0))
  const heaviest = Math.max(...weights)
  if (heaviest <= 0) return components
  return groups.filter((_, index) => weights[index] >= heaviest * minGroupAreaRatio).flat()
}
